import { Directive, inject } from "@angular/core";
import { LorenBaseFeature } from "../loren-base-feature/loren-base-feature";
import { Observable } from "rxjs";
import { first, take } from "rxjs/operators";
import {
  divIcon,
  LatLngLiteral, Layer,
  Map as LeafletMap,
  marker,
  Marker,
  Polyline,
} from "leaflet";
import * as L from "leaflet";
import "leaflet-draw";
import "leaflet.markercluster";
import { LorenMap } from "../../../features/loren-map/constants/loren-map.constants";
import { LorenFeature } from "../../enums/loren-feature.enum";
import { LorenLayer } from "../../services/loren-layer-service/interfaces/loren-layer.interface";
import { LorenAsset } from "../../../features/loren-assets/interfaces/loren-asset.interface";
import { LorenBreakpointObserverService } from "../../services/loren-breakpoint-observer-service/loren-breakpoint-observer.service";
import { LorenNavService } from "../../modules/loren-nav/components/loren-nav/services/loren-nav-service/loren-nav.service";
import { LorenLayerService } from "../../services/loren-layer-service/loren-layer.service";
import { LorenLayerUtilsService } from "../../services/loren-layer-service/services/loren-layer-utils-service/loren-layer-utils.service";
import { LorenFeatureToggleService } from "../../services/loren-feature-toggle-service/loren-feature-toggle.service";
import { LorenUserLocationService } from "../../services/loren-user-location-service/loren-user-location.service";
import {getMapDefaults} from "../../../features/asset-map/map.defaults";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Directive()
export abstract class LorenBaseMapFeature extends LorenBaseFeature {
  mapHeight$: Observable<number> =
    this.breakpointObserverService.contentWrapperHeight$;
  options = getMapDefaults();

  hasInteracted = false;
  showLayerSelectionDialog: boolean;
  allMapLayers: LorenLayer[] = [];
  markerClusterGroup: L.MarkerClusterGroup;
  markerClusterData: L.Marker[] = [];
  layerPolylineData: Polyline[] = [];
  markerClusterOptions: L.MarkerClusterGroupOptions = {
    animate: true,
    animateAddingMarkers: true,
  };
  lastSelectedLayers: LorenLayer[] = [];
  layerBtnBottomOffset: number;
  lorenFeatures = LorenFeature;

  protected mapInstance: LeafletMap;
  protected allLayersPolylines: Polyline[] = [];
  protected allLayersMarkers: Marker[] = [];

  protected breakpointObserverService = inject(LorenBreakpointObserverService);
  protected lorenNavService = inject(LorenNavService);
  protected lorenLayerService = inject(LorenLayerService);
  protected layerUtilsService = inject(LorenLayerUtilsService);
  protected toggleService = inject(LorenFeatureToggleService);
  protected locationService = inject(LorenUserLocationService);

  setUpMarkerClusterGroup() {
    this.markerClusterGroup = L.markerClusterGroup({
      removeOutsideVisibleBounds: true,
    });
  }

  markerClusterReady($event: L.MarkerClusterGroup) {
    this.markerClusterGroup = $event;
  }

  handleLayerSelection(selectedLayers: LorenLayer[]) {
    if (!this.mapInstance) {
      return;
    }

    this.lastSelectedLayers = selectedLayers;
    this.prepareLayerData();
    this.showLayerSelectionDialog = false;
  }

  onMapReady(map: LeafletMap) {
    this.toggleService.executeIfUserHasFeature(
      LorenFeature.GENERIC_MAP_LAYERS_VISUALIZATION,
      () => {
        this.lorenLayerService
          .getAllLayers()
          .pipe(take(1))
          .subscribe((s) => (this.allMapLayers = s.items));
      }
    );

    this.mapInstance = map;
    this.mapInstance.on('movestart zoomstart click drag', () => {
      this.hasInteracted = true;
    })

    this.onMapLoaded();
  }

  handleMapMove() {
    this.prepareLayerData();
  }

  handleLayerButtonBottomOffset() {
    this.layerBtnBottomOffset = 16 + this.getPanelHeight();
  }

  protected getPanelHeight() {
    return 0;
  }

  protected setInitialLocation() {
    this.locationService.getLocation()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => {
          if (res === LorenUserLocationService.defaultLocation) {
            this.mapInstance.setView(res, 8);
          } else {
            this.mapInstance.setView(res, 12);
          }
        });
  }

  protected abstract onMapLoaded(): void;

  protected deleteMapLayer(layer: Layer) {
    if (!layer) {
      return;
    }

    this.mapInstance.removeLayer(layer);
  }

  protected addMarkerPopup(
    mapMarker: Marker,
    popupTitle: string,
    lastLocation: LatLngLiteral,
    time?: string
  ) {
    let popUpContent = `<h4 style="font-size: 14px; font-weight: bold">${popupTitle}</h4><div style="font-size: 12px; font-weight: bold">${time}</div><div style="font-size: 12px">${lastLocation.lat} / ${lastLocation.lng}</div>`;
    popUpContent += `<div class="mt-3"><a target="_blank" href="${LorenMap.googleStreetViewLink}${lastLocation.lat},${lastLocation.lng}">Street view</a></div>`;

    const lastPopup = mapMarker.getPopup();
    let wasOpened = false;

    if (lastPopup) {
      wasOpened = lastPopup.isOpen();
    }

    mapMarker.closePopup();
    mapMarker.unbindPopup();
    mapMarker.bindPopup(popUpContent, { autoClose: true });

    if (wasOpened) {
      mapMarker.openPopup();
    }
  }

  protected createNewMarker(
    lastLocation: LatLngLiteral,
    asset: LorenAsset,
    color: string = null,
    imgSrc: string = null,
    timeForPopup: string = null
  ) {
    const image = imgSrc || asset.avatar || "/icon-96x96.png";
    const markerBackgroundColor = `background:` + color;
    const icon = divIcon({
      className: "custom-div-icon",
      html: `<div class='marker-pin' style=${
        color ? markerBackgroundColor : ""
      }></div><img src="${image}" alt="${asset.name}">`,
      iconSize: [30, 42],
      iconAnchor: [15, 42],
    });

    const mapMarker = marker(lastLocation, { icon });

    if(timeForPopup) {
      this.addMarkerPopup(mapMarker, asset.name, lastLocation, timeForPopup);
    }

    return mapMarker;
  }

  private prepareLayerData() {
    if (!this.lastSelectedLayers || this.lastSelectedLayers.length === 0) {
      this.clearLayersMarkers();
      this.clearLayersPolylines();
      return;
    }

    const currentBounds = this.mapInstance.getBounds();
    const zoom = this.mapInstance.getZoom();

    this.layerUtilsService
      .handleLayerSelection(this.lastSelectedLayers, currentBounds, zoom)
      .pipe(first())
      .subscribe((res) => {
        this.clearLayersPolylines();
        this.clearLayersMarkers();

        this.markerClusterData = res.markers;
        this.layerPolylineData = res.polylines;
        this.allLayersPolylines = res.polylines;
        this.allLayersMarkers = res.markers;

        this.layerPolylineData.forEach((p) => this.mapInstance.addLayer(p));
        this.markerClusterData.forEach((p) => this.mapInstance.addLayer(p));
      });
  }

  private clearLayersPolylines() {
    this.layerPolylineData.forEach((x) => this.mapInstance.removeLayer(x));
    this.allLayersPolylines = [];
    this.layerPolylineData = [];
  }

  private clearLayersMarkers() {
    this.markerClusterData = [];
    this.allLayersMarkers.forEach((x) => this.mapInstance.removeLayer(x));
    this.allLayersMarkers = [];
  }
}
